import React, { useEffect, useRef, useState } from 'react'

import { Field, Form, Formik, FormikHelpers } from 'formik'
import { Col, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { TagsInput } from 'react-tag-input-component'

import { useFeatures } from '../common/FeatureContext'
import FormErrorMessage from '../common/FormErrorMessage'
import { dateToIsoString, fetchClients } from '../common/helpers'
import { datePickerDateFormat } from '../common/i18n'
import NiceButton from '../common/NiceButton'
import { RequiredMark } from '../common/RequiredMark'
import { ClientFormWrapper } from '../common/styledComponents'
import axiosClient, { SERVER_URL } from '../service/axiosClient'
import ClientDTO from '../types/Client'
import PostDTO, {
    PostApprovalStatusType,
    PostFormData,
    PostStatusType,
    PostType,
    PublishFrequency,
    TiktokCreatorInfo
} from '../types/Post'
import Post from '../types/Post'
import SocialNetwork from '../types/SocialNetwork'

interface Props {
    id?: number | null
}

const socialNetworkOptions = [
    {value: SocialNetwork.INSTAGRAM, label: 'Instagram'},
    {value: SocialNetwork.FACEBOOK, label: 'Facebook'},
    {value: SocialNetwork.LINKED_IN, label: 'Linked In'},
    {value: SocialNetwork.TIKTOK, label: 'TikTok'},
]

const postTypeOptions = [
    {value: PostType.POST, label: 'Post'},
    {value: PostType.STORY, label: 'Story'},
    {value: PostType.REEL, label: 'Reel'},
]
const descriptionMaxLength: Record<string, number> = {
    [SocialNetwork.INSTAGRAM]: 2200,
    [SocialNetwork.FACEBOOK]: 63206,
    [SocialNetwork.TIKTOK]: 2200,
    [SocialNetwork.LINKED_IN]: 3000,
}

const LinkedInAuthorField = ({clientId, value, onChange}: {clientId: string, value: string, onChange: (urn: string) => void}) => {
    const [authors, setAuthors] = useState<{urn: string, name: string, type: string}[]>([])
    const {t} = useTranslation()

    useEffect(() => {
        if (clientId) {
            axiosClient.get(`linkedin/organizations?clientId=${clientId}`)
                .then(response => {
                    setAuthors(response.data)
                    if (!value && response.data.length > 0) {
                        onChange(response.data[0].urn)
                    }
                })
                .catch(err => {
                    console.error('Error fetching LinkedIn authors', err)
                    setAuthors([])
                })
        } else {
            setAuthors([])
        }
    }, [clientId])

    if (authors.length === 0) return null

    return (
        <div className="form-group">
            <label htmlFor="linkedinAuthorUrn">{t('profile_page')}</label>
            <select
                id="linkedinAuthorUrn"
                name="linkedinAuthorUrn"
                className="form-select"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">{t('select')}</option>
                {authors.map(author => (
                    <option key={author.urn} value={author.urn}>
                        {author.type === 'PERSON' ? t('personal_profile', {name: author.name}) : t('page_label', {name: author.name})}
                    </option>
                ))}
            </select>
        </div>
    )
}

const MetaPageField = ({clientId, socialNetwork, value, onChange}: {clientId: string, socialNetwork: SocialNetwork, value: string, onChange: (pageId: string) => void}) => {
    const [pages, setPages] = useState<{id: string, name: string, category: string, hasInstagram: boolean}[]>([])
    const {t} = useTranslation()

    useEffect(() => {
        if (clientId && (socialNetwork === SocialNetwork.FACEBOOK || socialNetwork === SocialNetwork.INSTAGRAM)) {
            axiosClient.get(`meta/pages?clientId=${clientId}`)
                .then(response => {
                    let availablePages = response.data
                    if (socialNetwork === SocialNetwork.INSTAGRAM) {
                        availablePages = availablePages.filter((p: any) => p.hasInstagram)
                    }
                    setPages(availablePages)
                    if (!value && availablePages.length > 0) {
                        onChange(availablePages[0].id)
                    }
                })
                .catch(err => {
                    console.error('Error fetching Meta pages', err)
                    setPages([])
                })
        } else {
            setPages([])
        }
    }, [clientId, socialNetwork])

    if (pages.length === 0) return null

    return (
        <div className="form-group">
            <label htmlFor="metaPageId">{t('facebook_page')}</label>
            <select
                id="metaPageId"
                name="metaPageId"
                className="form-select"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">{t('select')}</option>
                {pages.map(page => (
                    <option key={page.id} value={page.id}>
                        {page.name}{page.category ? ` (${page.category})` : ''}
                    </option>
                ))}
            </select>
            {socialNetwork === SocialNetwork.INSTAGRAM && pages.length === 0 && (
                <small className="text-warning">{t('no_instagram_business')}</small>
            )}
        </div>
    )
}

const TIKTOK_PRIVACY_LABEL_KEYS: Record<string, string> = {
    PUBLIC_TO_EVERYONE: 'tiktok_privacy_public',
    MUTUAL_FOLLOW_FRIENDS: 'tiktok_privacy_friends',
    FOLLOWER_OF_CREATOR: 'tiktok_privacy_followers',
    SELF_ONLY: 'tiktok_privacy_self',
}

const TIKTOK_SELF_ONLY = 'SELF_ONLY'
const TIKTOK_MUSIC_USAGE_URL = 'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en'
const TIKTOK_BRANDED_CONTENT_URL = 'https://www.tiktok.com/legal/page/global/bc-policy/en'

interface TiktokSettingsProps {
    clientId: string
    hasVideo: boolean
    videoTooLong: boolean
    creatorInfo: TiktokCreatorInfo | null
    setCreatorInfo: (info: TiktokCreatorInfo | null) => void
    values: PostFormData
    setFieldValue: (field: string, value: any) => void
}

/**
 * TikTok's content sharing guidelines require the creator to make every publishing decision inside
 * our app before the post is sent: which account receives it, the privacy level (no default), which
 * interactions are allowed, and whether the post is commercial content. This block renders that UI
 * and mirrors the rules TikTok enforces on the API side.
 */
const TiktokSettings = ({clientId, hasVideo, videoTooLong, creatorInfo, setCreatorInfo, values, setFieldValue}: TiktokSettingsProps) => {
    const {t} = useTranslation()
    const [isLoadingInfo, setIsLoadingInfo] = useState(false)

    useEffect(() => {
        if (!clientId) {
            setCreatorInfo(null)
            return
        }
        setIsLoadingInfo(true)
        axiosClient.get<TiktokCreatorInfo>(`tiktok/creator-info?clientId=${clientId}`)
            .then(response => setCreatorInfo(response.data))
            .catch(err => {
                console.error('Error fetching TikTok creator info', err)
                // Keep the HTTP status so a missing endpoint or a server error can be told
                // apart from TikTok itself refusing the account.
                const status = err?.response?.status
                setCreatorInfo({
                    available: false,
                    errorCode: status ? `http_${status}` : 'request_failed',
                    privacyLevelOptions: [],
                    commentDisabled: false,
                    duetDisabled: false,
                    stitchDisabled: false,
                })
            })
            .finally(() => setIsLoadingInfo(false))
    }, [clientId])

    // A creator who turned an interaction off account-wide must not have it pre-enabled here.
    useEffect(() => {
        if (!creatorInfo?.available) return
        if (creatorInfo.commentDisabled && values.tiktokAllowComment) setFieldValue('tiktokAllowComment', false)
        if (creatorInfo.duetDisabled && values.tiktokAllowDuet) setFieldValue('tiktokAllowDuet', false)
        if (creatorInfo.stitchDisabled && values.tiktokAllowStitch) setFieldValue('tiktokAllowStitch', false)
    }, [creatorInfo, values.tiktokAllowComment, values.tiktokAllowDuet, values.tiktokAllowStitch])

    // Branded content cannot be private - clear it as soon as the creator picks "only me".
    useEffect(() => {
        if (values.tiktokPrivacyLevel === TIKTOK_SELF_ONLY && values.tiktokBrandedContent) {
            setFieldValue('tiktokBrandedContent', false)
        }
    }, [values.tiktokPrivacyLevel])

    // The TikTok fields are mandatory, so never leave this area silently empty - say why.
    if (!clientId) {
        return (
            <div className="alert alert-info" role="alert">
                {t('tiktok_select_client_first')}
            </div>
        )
    }

    if (isLoadingInfo || !creatorInfo) {
        return <div className="form-group"><small className="text-muted">{t('tiktok_loading_creator_info')}</small></div>
    }

    if (!creatorInfo.available) {
        const messageKey = creatorInfo.errorCode === 'spam_risk_too_many_posts'
            ? 'tiktok_error_too_many_posts'
            : creatorInfo.errorCode === 'not_connected'
                ? 'tiktok_error_not_connected'
                : creatorInfo.errorCode === 'token_expired'
                    ? 'tiktok_error_token_expired'
                    : 'tiktok_error_unavailable'
        return (
            <div className="alert alert-warning" role="alert">
                {t(messageKey)}
                {creatorInfo.errorCode && (
                    <small className="d-block mt-1">{t('tiktok_error_code')}: {creatorInfo.errorCode}</small>
                )}
            </div>
        )
    }

    const commercialContent = !!values.tiktokCommercialContent
    const brandOrganic = !!values.tiktokBrandOrganic
    const brandedContent = !!values.tiktokBrandedContent
    const missingCommercialChoice = commercialContent && !brandOrganic && !brandedContent

    const commercialLabelKey = brandedContent
        ? 'tiktok_label_paid_partnership'
        : brandOrganic
            ? 'tiktok_label_promotional'
            : null

    return (
        <div className="border rounded p-3 mb-3">
            <div className="d-flex align-items-center mb-3">
                {creatorInfo.creatorAvatarUrl && (
                    <img src={creatorInfo.creatorAvatarUrl} alt=""
                         style={{width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover'}}
                         className="me-2"/>
                )}
                <div>
                    <strong>{t('tiktok_posting_as')}</strong>{' '}
                    {creatorInfo.creatorNickname}
                    {creatorInfo.creatorUsername ? ` (@${creatorInfo.creatorUsername})` : ''}
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="tiktokPrivacyLevel">{t('tiktok_privacy_level')}</label>
                <select
                    id="tiktokPrivacyLevel"
                    name="tiktokPrivacyLevel"
                    className="form-select"
                    value={values.tiktokPrivacyLevel || ''}
                    onChange={(e) => setFieldValue('tiktokPrivacyLevel', e.target.value)}
                >
                    <option value="">{t('select')}</option>
                    {creatorInfo.privacyLevelOptions.map(option => (
                        <option
                            key={option}
                            value={option}
                            disabled={option === TIKTOK_SELF_ONLY && brandedContent}
                            title={option === TIKTOK_SELF_ONLY && brandedContent ? t('tiktok_branded_not_private') : undefined}
                        >
                            {t(TIKTOK_PRIVACY_LABEL_KEYS[option] || option)}
                        </option>
                    ))}
                </select>
                <FormErrorMessage name="tiktokPrivacyLevel"/>
                {brandedContent && (
                    <small className="d-block text-muted mt-1">{t('tiktok_branded_not_private')}</small>
                )}
            </div>

            <div className="form-group mt-3">
                <label className="d-block">{t('tiktok_interactions')}</label>
                <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="tiktokAllowComment"
                           checked={!!values.tiktokAllowComment}
                           disabled={creatorInfo.commentDisabled}
                           onChange={(e) => setFieldValue('tiktokAllowComment', e.target.checked)}/>
                    <label className={`form-check-label${creatorInfo.commentDisabled ? ' text-muted' : ''}`}
                           htmlFor="tiktokAllowComment">
                        {t('tiktok_allow_comment')}
                        {creatorInfo.commentDisabled && ` (${t('tiktok_disabled_by_creator')})`}
                    </label>
                </div>
                {hasVideo && (
                    <>
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="tiktokAllowDuet"
                                   checked={!!values.tiktokAllowDuet}
                                   disabled={creatorInfo.duetDisabled}
                                   onChange={(e) => setFieldValue('tiktokAllowDuet', e.target.checked)}/>
                            <label className={`form-check-label${creatorInfo.duetDisabled ? ' text-muted' : ''}`}
                                   htmlFor="tiktokAllowDuet">
                                {t('tiktok_allow_duet')}
                                {creatorInfo.duetDisabled && ` (${t('tiktok_disabled_by_creator')})`}
                            </label>
                        </div>
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="tiktokAllowStitch"
                                   checked={!!values.tiktokAllowStitch}
                                   disabled={creatorInfo.stitchDisabled}
                                   onChange={(e) => setFieldValue('tiktokAllowStitch', e.target.checked)}/>
                            <label className={`form-check-label${creatorInfo.stitchDisabled ? ' text-muted' : ''}`}
                                   htmlFor="tiktokAllowStitch">
                                {t('tiktok_allow_stitch')}
                                {creatorInfo.stitchDisabled && ` (${t('tiktok_disabled_by_creator')})`}
                            </label>
                        </div>
                    </>
                )}
            </div>

            <div className="form-group mt-3">
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="tiktokCommercialContent"
                           checked={commercialContent}
                           onChange={(e) => {
                               setFieldValue('tiktokCommercialContent', e.target.checked)
                               if (!e.target.checked) {
                                   setFieldValue('tiktokBrandOrganic', false)
                                   setFieldValue('tiktokBrandedContent', false)
                               }
                           }}/>
                    <label className="form-check-label" htmlFor="tiktokCommercialContent">
                        {t('tiktok_disclose_content')}
                    </label>
                </div>
                <small className="d-block text-muted">{t('tiktok_disclose_content_hint')}</small>

                {commercialContent && (
                    <div className="ms-4 mt-2">
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="tiktokBrandOrganic"
                                   checked={brandOrganic}
                                   onChange={(e) => setFieldValue('tiktokBrandOrganic', e.target.checked)}/>
                            <label className="form-check-label" htmlFor="tiktokBrandOrganic">
                                {t('tiktok_your_brand')}
                            </label>
                            <small className="d-block text-muted">{t('tiktok_your_brand_hint')}</small>
                        </div>
                        <div className="form-check mt-2">
                            <input className="form-check-input" type="checkbox" id="tiktokBrandedContent"
                                   checked={brandedContent}
                                   disabled={values.tiktokPrivacyLevel === TIKTOK_SELF_ONLY}
                                   onChange={(e) => setFieldValue('tiktokBrandedContent', e.target.checked)}/>
                            <label className={`form-check-label${values.tiktokPrivacyLevel === TIKTOK_SELF_ONLY ? ' text-muted' : ''}`}
                                   htmlFor="tiktokBrandedContent"
                                   title={values.tiktokPrivacyLevel === TIKTOK_SELF_ONLY ? t('tiktok_branded_not_private') : undefined}>
                                {t('tiktok_branded_content')}
                            </label>
                            <small className="d-block text-muted">{t('tiktok_branded_content_hint')}</small>
                        </div>
                        {missingCommercialChoice && (
                            <small className="d-block text-danger mt-2">{t('tiktok_commercial_choice_required')}</small>
                        )}
                        {commercialLabelKey && (
                            <small className="d-block mt-2">{t(commercialLabelKey)}</small>
                        )}
                    </div>
                )}
            </div>

            {hasVideo && creatorInfo.maxVideoPostDurationSec ? (
                <small className={`d-block mt-2 ${videoTooLong ? 'text-danger' : 'text-muted'}`}>
                    {t('tiktok_max_video_duration', {seconds: creatorInfo.maxVideoPostDurationSec})}
                </small>
            ) : null}

            <small className="d-block text-muted mt-2">{t('tiktok_processing_hint')}</small>

            <small className="d-block text-muted mt-3">
                {brandedContent ? (
                    <>
                        {t('tiktok_declaration_branded_prefix')}{' '}
                        <a href={TIKTOK_BRANDED_CONTENT_URL} target="_blank" rel="noreferrer">{t('tiktok_branded_content_policy')}</a>
                        {' '}{t('and')}{' '}
                        <a href={TIKTOK_MUSIC_USAGE_URL} target="_blank" rel="noreferrer">{t('tiktok_music_usage_confirmation')}</a>
                    </>
                ) : (
                    <>
                        {t('tiktok_declaration_prefix')}{' '}
                        <a href={TIKTOK_MUSIC_USAGE_URL} target="_blank" rel="noreferrer">{t('tiktok_music_usage_confirmation')}</a>
                    </>
                )}
            </small>
        </div>
    )
}

export const PostForm = ({id}: Props) => {
    const { isFeatureEnabled, features, isLoading: featuresLoading } = useFeatures()
    const metaEnabled = isFeatureEnabled('meta_integration')

    console.log('PostForm - featuresLoading:', featuresLoading, 'features:', features, 'metaEnabled:', metaEnabled)

    const filteredSocialNetworkOptions = socialNetworkOptions.filter(option =>
        metaEnabled || (option.value !== SocialNetwork.INSTAGRAM && option.value !== SocialNetwork.FACEBOOK)
    )

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const isSubmittingRef = useRef(false)
    const [searchParams,] = useSearchParams()
    const queryClientId = searchParams.get('clientId')
    const queryStart = searchParams.get('start')
    const queryEnd = searchParams.get('end')

    const navigate = useNavigate()
    const [clients, setClients] = useState<ClientDTO[]>([])
    const [post, setPost] = useState<PostFormData>({
        title: '',
        description: '',
        clientId: queryClientId || '',
        postType: '' as unknown as PostType,
        socialNetwork: '' as unknown as SocialNetwork,
        publishFrequency: '' as unknown as PublishFrequency,
        postApprovalStatus: '' as unknown as PostApprovalStatusType,
        postStatus: PostStatusType.NOT_PUBLISHED,
        photo: '',
        showInCalendar: false,
        hashTags: [],
        publishAt: '',
        publishFrom: queryStart ? new Date(queryStart) : null,
        publishUntil: queryEnd ? new Date(queryEnd) : null,
        linkedinAuthorUrn: '',
        metaPageId: '',
        tiktokPrivacyLevel: '',
        tiktokAllowComment: false,
        tiktokAllowDuet: false,
        tiktokAllowStitch: false,
        tiktokCommercialContent: false,
        tiktokBrandOrganic: false,
        tiktokBrandedContent: false,
    } as PostFormData)
    const {t} = useTranslation()
    const [key, setKey] = useState('initial')
    const [mediaFiles, setMediaFiles] = useState<{filename: string, url: string, isVideo: boolean, thumbnailUrl?: string, durationSec?: number}[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState<TiktokCreatorInfo | null>(null)

    const hasVideo = mediaFiles.some(f => f.isVideo)
    const maxTiktokVideoDuration = tiktokCreatorInfo?.maxVideoPostDurationSec
    const videoTooLong = !!maxTiktokVideoDuration
        && mediaFiles.some(f => f.isVideo && !!f.durationSec && f.durationSec > maxTiktokVideoDuration)


    useEffect(() => {
        fetchClients().then(setClients).catch(console.error)
    }, [])

    /**
     * Grabs a frame to preview the video, or '' when the browser cannot load it.
     *
     * Safari fires error rather than loadeddata when it cannot read the video, and this used to
     * wait only for success - so the promise never settled and the upload spinner never stopped.
     */
    const generateVideoThumbnail = (url: string): Promise<string> => {
        return new Promise((resolve) => {
            const video = document.createElement('video')
            const timeout = window.setTimeout(() => resolve(''), 10000)
            const finish = (thumbnail: string) => {
                window.clearTimeout(timeout)
                resolve(thumbnail)
            }
            video.crossOrigin = 'anonymous'
            video.muted = true
            video.playsInline = true
            video.preload = 'auto'
            video.addEventListener('loadeddata', () => {
                video.currentTime = 0.1
            })
            video.addEventListener('seeked', () => {
                try {
                    const canvas = document.createElement('canvas')
                    canvas.width = video.videoWidth
                    canvas.height = video.videoHeight
                    const ctx = canvas.getContext('2d')
                    if (ctx) {
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
                        finish(canvas.toDataURL('image/png'))
                    } else {
                        finish('')
                    }
                } catch {
                    finish('')
                }
            })
            video.addEventListener('error', () => finish(''))
            video.src = url
            video.load()
        })
    }

    /** Video length is needed to check the post against TikTok's max_video_post_duration_sec. */
    const readVideoDuration = (url: string): Promise<number | undefined> => {
        return new Promise((resolve) => {
            const video = document.createElement('video')
            const timeout = window.setTimeout(() => resolve(undefined), 10000)
            video.preload = 'metadata'
            video.addEventListener('loadedmetadata', () => {
                window.clearTimeout(timeout)
                resolve(isFinite(video.duration) ? video.duration : undefined)
            })
            video.addEventListener('error', () => {
                window.clearTimeout(timeout)
                resolve(undefined)
            })
            video.src = url
            video.load()
        })
    }

    useEffect(() => {
        if (id && id > 0) {
            axiosClient.get('post/' + id)
                .then(async response => {
                    console.log('response', response)
                    const postTmp = response.data
                    setPost({
                        ...postTmp,
                        hashTags: postTmp.hashTags.split(','),
                        publishUntil: postTmp.publishUntil ? new Date(postTmp.publishUntil) : null,
                        publishFrom: postTmp.publishFrom ? new Date(postTmp.publishFrom) : null,
                        publishFrequency: postTmp.publishFrequency as PublishFrequency,
                        socialNetwork: postTmp.socialNetwork as SocialNetwork
                    })
                    if (postTmp.photo) {
                        const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv']
                        const filenames = postTmp.photo.split(',')
                        const files = await Promise.all(filenames.map(async (filename: string) => {
                            const url = `${SERVER_URL}/storage/${filename}`
                            const isVideoFile = videoExtensions.some(ext => filename.toLowerCase().endsWith(ext))
                            let thumbnailUrl: string | undefined
                            let durationSec: number | undefined
                            if (isVideoFile) {
                                try {
                                    thumbnailUrl = await generateVideoThumbnail(url)
                                    durationSec = await readVideoDuration(url)
                                } catch { /* ignore */ }
                            }
                            return { filename, url, isVideo: isVideoFile, thumbnailUrl, durationSec }
                        }))
                        setMediaFiles(files)
                    }
                })
        }
    }, [id])

    const submit = async (values: PostFormData, actions: FormikHelpers<PostFormData>) => {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        console.log('values', values)


        const isTiktok = values.socialNetwork === SocialNetwork.TIKTOK

        try {
            setIsLoading(true)
            const request: Omit<PostDTO, 'id' | 'clientLastName' | 'clientFirstName'> & { id?: number } = {
                title: values.title ?? '',
                description: values.description ?? '',
                clientId: values.clientId as unknown as number,
                postType: values.postType as PostType,
                socialNetwork: values.socialNetwork as SocialNetwork,
                publishFrequency: values.publishFrequency as PublishFrequency,
                postApprovalStatus: (values.postApprovalStatus || PostApprovalStatusType.APPROVED) as PostApprovalStatusType,
                postStatus: values.postStatus as PostStatusType,
                photo: values.photo as string,
                showInCalendar: values.showInCalendar,
                hashTags: '',
                publishAt: values.publishAt,
                publishFrom: '',
                publishUntil: '',
                linkedinAuthorUrn: values.socialNetwork === SocialNetwork.LINKED_IN ? values.linkedinAuthorUrn : '',
                metaPageId: (values.socialNetwork === SocialNetwork.FACEBOOK || values.socialNetwork === SocialNetwork.INSTAGRAM) ? values.metaPageId : '',
                tiktokPrivacyLevel: isTiktok ? (values.tiktokPrivacyLevel ?? '') : '',
                tiktokAllowComment: isTiktok && !!values.tiktokAllowComment,
                tiktokAllowDuet: isTiktok && !!values.tiktokAllowDuet,
                tiktokAllowStitch: isTiktok && !!values.tiktokAllowStitch,
                tiktokCommercialContent: isTiktok && !!values.tiktokCommercialContent,
                tiktokBrandOrganic: isTiktok && !!values.tiktokBrandOrganic,
                tiktokBrandedContent: isTiktok && !!values.tiktokBrandedContent,
            }

            if (id && id > 0) {
                request.id = id
            }

            request.hashTags = values.hashTags?.join(',') ?? ''

            // if (values.publishAt) {
            //     const publishAt = new Date(values.publishAt)
            //     const hours = publishAt.getHours().toString().padStart(2, '0')
            //     const minutes = publishAt.getMinutes().toString().padStart(2, '0')
            //
            //     request.publishAt = `${hours}:${minutes}`
            // }
            if (values.publishFrom) {
                request.publishFrom = dateToIsoString((new Date(values.publishFrom)))
            }
            if (values.publishUntil) {
                request.publishUntil = dateToIsoString((new Date(values.publishUntil)))
            }

            const response = await axiosClient.post('post', request)

            if (id) {
                window.scrollTo(0, 0)
                navigate('/app/posts')
                toast.success(t('post_updated_success'))
            } else if (response.data > 0) {
                navigate('/app/posts')
                toast.success(t('post_created_success'))
            }
            // actions.resetForm()
        } catch (error: any) {
            toast.error(t('post_save_error'))
        } finally {
            setIsLoading(false)
            isSubmittingRef.current = false
        }
    }

    const validate = (values: Partial<PostFormData>) => {
        const errors: any = {}

        if (!values?.clientId) {
            errors.clientId = t('required_field_error')
        }
        if (!values?.socialNetwork) {
            errors.socialNetwork = t('required_field_error')
        }
        if (!values?.publishFrequency) {
            errors.publishFrequency = t('required_field_error')
        }
        if (!values?.publishAt) {
            errors.publishAt = t('required_field_error')
        }
        if (!values?.title?.trim()) {
            errors.title = t('required_field_error')
        }

        if (!values?.description?.trim()) {
            errors.description = t('required_field_error')
        }

        // TikTok requires an explicit privacy choice and, when commercial content is disclosed,
        // at least one of "Your brand" / "Branded content".
        if (values?.socialNetwork === SocialNetwork.TIKTOK && tiktokCreatorInfo?.available) {
            if (!values?.tiktokPrivacyLevel) {
                errors.tiktokPrivacyLevel = t('required_field_error')
            }
            if (values?.tiktokCommercialContent && !values?.tiktokBrandOrganic && !values?.tiktokBrandedContent) {
                errors.tiktokCommercialContent = t('tiktok_commercial_choice_required')
            }
        }

        return errors
    }

    return (
        <ClientFormWrapper>
            <div className="row d-flex">
                <div className="col-md-12">
                    <Formik
                        initialValues={post}
                        enableReinitialize
                        validate={validate}
                        onSubmit={submit}
                    >{({values, setFieldValue}) => (<>
                        <Form>
                            <Row>
                                <Col>
                                    <div
                                        className={'form-group'}>
                                        <label htmlFor="clientId">
                                            {t('client')}
                                        <RequiredMark/></label>
                                        <Field as="select" id="clientId" name="clientId" className="form-select">
                                            <option value="">{t('select')}</option>
                                            {clients.map((option) => (
                                                <option key={option.id}
                                                        value={option.id}>{option.companyName || (`${option.firstName} ${option.lastName}`)}</option>))}
                                        </Field>
                                        <FormErrorMessage name="clientId"/>
                                    </div>
                                </Col>
                                <Col>
                                    <div
                                        className={'form-group'}>
                                        <label htmlFor="socialNetwork">
                                            {t('social_network')}
                                        <RequiredMark/></label>
                                        <Field as="select" id="socialNetwork" name="socialNetwork"
                                               className="form-select">
                                            <option value="">{t('select')}</option>
                                            {filteredSocialNetworkOptions.map((option) => (
                                                <option key={option.value}
                                                        value={option.value}>{option.label}</option>))}
                                        </Field>
                                        <FormErrorMessage name="socialNetwork"/>
                                    </div>
                                </Col>
                            </Row>

                            {values.socialNetwork === SocialNetwork.LINKED_IN && (
                                <LinkedInAuthorField
                                    clientId={values.clientId}
                                    value={values.linkedinAuthorUrn}
                                    onChange={(urn) => setFieldValue('linkedinAuthorUrn', urn)}
                                />
                            )}

                            {values.socialNetwork === SocialNetwork.TIKTOK && (
                                <TiktokSettings
                                    clientId={values.clientId}
                                    hasVideo={hasVideo}
                                    videoTooLong={videoTooLong}
                                    creatorInfo={tiktokCreatorInfo}
                                    setCreatorInfo={setTiktokCreatorInfo}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                />
                            )}

                            {(values.socialNetwork === SocialNetwork.FACEBOOK || values.socialNetwork === SocialNetwork.INSTAGRAM) && values.clientId && (
                                <MetaPageField
                                    clientId={values.clientId}
                                    socialNetwork={values.socialNetwork as SocialNetwork}
                                    value={values.metaPageId}
                                    onChange={(pageId) => setFieldValue('metaPageId', pageId)}
                                />
                            )}

                            <div
                                className={'form-group'}>
                                <label htmlFor="postType">{t('post_type')}</label>
                                <Field as="select" id="postType" name="postType" className="form-select">
                                    <option value="">{t('select')}</option>
                                    {postTypeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>))}
                                </Field>
                                <FormErrorMessage name="postType"/>
                            </div>

                            <div
                                className={'form-group'}>
                                <label htmlFor="postApprovalStatus">{t('approval_status')}</label>
                                <Field as="select" id="postApprovalStatus" name="postApprovalStatus" className="form-select">
                                    <option value="">{t('select')}</option>
                                    <option value={PostApprovalStatusType.APPROVED}>{t('approval_approved')}</option>
                                    <option value={PostApprovalStatusType.DECLINED}>{t('approval_declined')}</option>
                                    <option value={PostApprovalStatusType.CHANGE_REQUIRED}>{t('approval_change_required')}</option>
                                </Field>
                                <FormErrorMessage name="postApprovalStatus"/>
                            </div>

                            <div
                                className={'form-group'}>
                                <label htmlFor="publishFrequency">
                                    {t('publish_frequency')}
                                <RequiredMark/></label>
                                <Field as="select" id="publishFrequency" name="publishFrequency"
                                       className="form-select">
                                    <option value="">{t('select')}</option>
                                    <option value={PublishFrequency.ONCE}>{t('freq_once')}</option>
                                    <option value={PublishFrequency.DAILY}>{t('freq_daily')}</option>
                                    <option value={PublishFrequency.WEEKLY}>{t('freq_weekly')}</option>
                                    <option value={PublishFrequency.MONTHLY}>{t('freq_monthly')}</option>
                                </Field>
                                <FormErrorMessage name="publishFrequency"/>
                            </div>

                            <div className="form-group">
                                <label htmlFor="title">{t('post_title')}<RequiredMark/></label>
                                <Field id="title" name="title" className="form-control"/>
                                <FormErrorMessage name="title"/>
                            </div>
                            <div className="form-group">
                                <label htmlFor="description">{t('description')}<RequiredMark/></label>
                                <Field as="textarea" rows={5} id="description" name="description"
                                       className="form-control"
                                       maxLength={values.socialNetwork ? descriptionMaxLength[values.socialNetwork] : undefined}/>
                                {values.socialNetwork && descriptionMaxLength[values.socialNetwork] && (
                                    <small className={`d-block text-end mt-1 ${(values.description?.length || 0) > descriptionMaxLength[values.socialNetwork] ? 'text-danger' : 'text-muted'}`}>
                                        {values.description?.length || 0} / {descriptionMaxLength[values.socialNetwork]}
                                    </small>
                                )}
                                <FormErrorMessage name="description"/>
                            </div>

                            <div className="form-group form-check">
                                <Field type="checkbox" id="showInCalendar" className="form-check-input"
                                       name="showInCalendar"/>
                                <label htmlFor={'showInCalendar'} className="form-check-label">
                                    {t('show_in_calendar')}
                                </label>
                            </div>


                            <div className="form-group">
                                <label>{t('hashtags')}</label>
                                <TagsInput
                                    value={values.hashTags}
                                    onChange={(tags) => {
                                        const lastTag = tags[tags.length - 1]
                                        if (lastTag && lastTag.includes('#')) {
                                            const parsed = lastTag
                                                .split('#')
                                                .map(t => t.trim())
                                                .filter(t => t.length > 0)
                                            const existingTags = tags.slice(0, -1)
                                            setFieldValue('hashTags', [...existingTags, ...parsed])
                                        } else {
                                            setFieldValue('hashTags', tags)
                                        }
                                    }}
                                    name="hashtags"
                                    placeHolder={t('enter_and_press')}
                                />
                                <small className="text-muted">{t('hashtag_confirm_hint')}</small>
                            </div>

                            {/*<h4 className="text-left">Významné udalosti</h4>*/}
                            <div className="row d-flex justify-content-center">
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="publishAt">{t('publish_time')}<RequiredMark/></label>
                                        <input
                                            className="form-control"
                                            type="time"
                                            value={values.publishAt}
                                            id="publishAt"
                                            name="publishAt"
                                            onChange={(e) => setFieldValue('publishAt', e.target.value)}/>
                                        {/*<DatePicker*/}
                                        {/*    showTimeSelect*/}
                                        {/*    showTimeSelectOnly*/}
                                        {/*    timeIntervals={1}*/}
                                        {/*    timeCaption="Time"*/}
                                        {/*    dateFormat="HH:mm"*/}
                                        {/*    locale="sk"*/}
                                        {/*    selected={(values.publishAt && new Date(values.publishAt)) || null}*/}
                                        {/*    onChange={(value: any) => {*/}
                                        {/*        console.log('value', value)*/}
                                        {/*        setFieldValue('publishAt', value)*/}
                                        {/*    }}*/}
                                        {/*    id="publishAt"*/}
                                        {/*    name="publishAt"*/}
                                        {/*    className="form-control"/>*/}
                                        <FormErrorMessage name="publishAt"/>
                                    </div>

                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="publishFrom">{t('date_from')}</label>
                                        <DatePicker
                                            scrollableYearDropdown
                                            yearDropdownItemNumber={100}
                                            locale="sk"
                                            dateFormat={datePickerDateFormat}
                                            showYearDropdown
                                            selected={(values.publishFrom && new Date(values.publishFrom)) || null}
                                            onChange={(value: any) => {
                                                setFieldValue('publishFrom', value)
                                            }}
                                            id="publishFrom"
                                            name="publishFrom"
                                            className="form-control"/>
                                        <FormErrorMessage name="publishFrom"/>
                                    </div>

                                </div>
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="publishUntil">{t('date_to')}</label>
                                        <DatePicker
                                            scrollableYearDropdown
                                            yearDropdownItemNumber={100}
                                            locale="sk"
                                            dateFormat={datePickerDateFormat}
                                            showYearDropdown
                                            selected={(values.publishUntil && new Date(values.publishUntil)) || null}
                                            onChange={(value: any) => {
                                                setFieldValue('publishUntil', value)
                                            }}
                                            id="publishUntil"
                                            name="publishUntil"
                                            className="form-control"/>
                                        <FormErrorMessage name="publishUntil"/>
                                    </div>

                                </div>
                            </div>

                            <div className="form-group">
                                <label
                                    htmlFor="file">{t('file_label')}</label>
                                {(values.socialNetwork === SocialNetwork.FACEBOOK ||
                                  values.socialNetwork === SocialNetwork.INSTAGRAM ||
                                  values.socialNetwork === SocialNetwork.LINKED_IN) && (
                                    <small className="d-block text-muted mb-2">
                                        {values.socialNetwork === SocialNetwork.INSTAGRAM
                                            ? t('carousel_hint_instagram')
                                            : values.socialNetwork === SocialNetwork.FACEBOOK
                                                ? t('carousel_hint_facebook')
                                                : t('carousel_hint_linkedin')}
                                    </small>
                                )}
                                {mediaFiles.length > 0 && (
                                    <div className="d-flex flex-wrap gap-3 mb-3">
                                        {mediaFiles.map((media, index) => (
                                            <div key={media.filename} className="position-relative" style={{width: '100px'}}>
                                                <span className="position-absolute top-0 start-0 badge bg-secondary" style={{fontSize: '10px', zIndex: 1}}>
                                                    {index + 1}
                                                </span>
                                                {media.isVideo ? (
                                                    media.thumbnailUrl ? (
                                                        <img src={media.thumbnailUrl} style={{width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px'}}/>
                                                    ) : (
                                                        <video src={media.url} style={{width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px'}} muted playsInline preload="metadata"/>
                                                    )
                                                ) : (
                                                    <img src={media.url} style={{width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px'}}/>
                                                )}
                                                <div className="d-flex gap-1 mt-1 justify-content-center">
                                                    {index > 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary px-1 py-0"
                                                            title={t('move_left', 'Move left')}
                                                            onClick={() => {
                                                                const newFiles = [...mediaFiles]
                                                                const temp = newFiles[index - 1]
                                                                newFiles[index - 1] = newFiles[index]
                                                                newFiles[index] = temp
                                                                setMediaFiles(newFiles)
                                                                setFieldValue('photo', newFiles.map(f => f.filename).join(','))
                                                            }}
                                                        >&larr;</button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger px-1 py-0"
                                                        title={t('delete')}
                                                        onClick={() => {
                                                            const newFiles = mediaFiles.filter((_, i) => i !== index)
                                                            setMediaFiles(newFiles)
                                                            setFieldValue('photo', newFiles.length > 0 ? newFiles.map(f => f.filename).join(',') : null)
                                                        }}
                                                    >&times;</button>
                                                    {index < mediaFiles.length - 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary px-1 py-0"
                                                            title={t('move_right', 'Move right')}
                                                            onClick={() => {
                                                                const newFiles = [...mediaFiles]
                                                                const temp = newFiles[index + 1]
                                                                newFiles[index + 1] = newFiles[index]
                                                                newFiles[index] = temp
                                                                setMediaFiles(newFiles)
                                                                setFieldValue('photo', newFiles.map(f => f.filename).join(','))
                                                            }}
                                                        >&rarr;</button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {mediaFiles.length > 0 && (
                                    <button
                                        onClick={() => {
                                            setMediaFiles([])
                                            setFieldValue('photo', null)
                                        }}
                                        type="button"
                                        className="button btn btn-primary btn-amani mb-2">{t('delete_all', 'Delete all')}</button>
                                )}

                                <label className="btn btn-like-input d-block mt-1"
                                       htmlFor="file">{t('upload_file')}</label>
                                {isUploading && (
                                    <div className="d-flex align-items-center mt-2">
                                        <div className="loader" style={{width: '20px', height: '20px'}}/>
                                        <span className="ms-2">{t('uploading')}</span>
                                    </div>
                                )}
                                <input className={'d-none'} id="file" key={key} name="file" type="file"
                                       multiple
                                       onChange={async (event) => {
                                           if (event.target.files && event.target.files.length > 0) {
                                               setIsUploading(true)
                                               const newFiles = [...mediaFiles]
                                               try {
                                                   for (let i = 0; i < event.target.files.length; i++) {
                                                       const file = event.target.files[i]
                                                       const fileIsVideo = file.type.startsWith('video/')
                                                       const formData = new FormData()
                                                       formData.append('file', file)

                                                       const resp = await axiosClient.post('storage', formData)
                                                       const filename = resp.data
                                                       const url = `${SERVER_URL}/storage/${filename}`
                                                       let thumbnailUrl: string | undefined
                                                       let durationSec: number | undefined

                                                       if (fileIsVideo) {
                                                           try {
                                                               thumbnailUrl = await generateVideoThumbnail(url)
                                                               durationSec = await readVideoDuration(url)
                                                           } catch { /* ignore */ }
                                                       }

                                                       newFiles.push({ filename, url, isVideo: fileIsVideo, thumbnailUrl, durationSec })
                                                   }
                                                   setMediaFiles(newFiles)
                                                   setFieldValue('photo', newFiles.map(f => f.filename).join(','))
                                               } finally {
                                                   setIsUploading(false)
                                                   setKey(Date.now().toString())
                                               }
                                           }
                                       }}/>
                            </div>

                            {(() => {
                                const isTiktok = values.socialNetwork === SocialNetwork.TIKTOK
                                // TikTok forbids publishing when the creator hit the posting limit,
                                // when the video is longer than the account allows, or when commercial
                                // content is disclosed without saying what it promotes.
                                const tiktokBlocked = isTiktok && (
                                    (!!tiktokCreatorInfo && !tiktokCreatorInfo.available)
                                    || videoTooLong
                                    || (!!values.tiktokCommercialContent && !values.tiktokBrandOrganic && !values.tiktokBrandedContent)
                                )
                                return (
                                    <NiceButton isLoading={isLoading} type="submit" disabled={tiktokBlocked}
                                                title={tiktokBlocked ? t('tiktok_publish_blocked') : undefined}>
                                        {t('save')}
                                    </NiceButton>
                                )
                            })()}
                        </Form>
                    </>)}

                    </Formik>
                </div>
            </div>
        </ClientFormWrapper>
    )
}